<template>
  <ClientOnly>
    <div class="header-profile">
      <template v-if="showAuthUi">
        <template v-if="isPersonal">
          <ClinicBranchSwitcher
            v-if="showBranchSwitcherInHeader"
            variant="header"
            :title="branchSwitcherTitle"
            class="header-profile__branch-switcher"
          />
          <Button
            v-else
            text="Выйти"
            isButton
            classList="outline header-profile__logout"
            @click="showLogoutModal"
            v-analytics:header="{content: 'logout'}"
          >
            <SvgoLogout/>
          </Button>
        </template>
        <template v-else>
          <Button text="Личный кабинет" :href="profileLink" classList="outline header-profile__button" v-analytics:header="{content: 'personal'}"/>
        </template>
        <NuxtLink :to="profileLink" class="header-profile__icon" aria-label="Личный кабинет" v-analytics:header="{content: 'personal'}"><SvgoUserFilled/></NuxtLink>
      </template>
      <template v-else>
        <Button text="Войти" :isButton="true" classList="outline header-profile__button" @click="showAuthForm" v-analytics:header="{content: 'login'}"/>
        <button type="button" class="header-profile__icon" aria-label="Войти" @click="showAuthForm" v-analytics:header="{content: 'login'}"><SvgoUserRound filled/></button>
      </template>
    </div>
    <template #fallback>
      <div class="header-profile header-profile--fallback" aria-hidden="true">
        <span class="button outline header-profile__button">
          <span class="button-text header-profile__fallback-login">Войти</span>
          <span class="button-text header-profile__fallback-account">Личный кабинет</span>
        </span>
        <span class="header-profile__icon">
          <SvgoUserRound filled class="header-profile__fallback-login"/>
          <SvgoUserFilled class="header-profile__fallback-account"/>
        </span>
      </div>
    </template>
  </ClientOnly>
</template>

<script setup lang="ts">
import {useModal} from 'vue-final-modal'
import {GAevent} from "~/utils/metrica-goals";
import type {TUserProfile, TUserRole} from "~/types/user";

const FormAuthPhone = defineAsyncComponent(() => import('~/components/Modal/FormAuthPhone.vue'));
const FormAuthClinic = defineAsyncComponent(() => import('~/components/Modal/FormAuthClinic.vue'));
const FormLogout = defineAsyncComponent(() => import('~/components/Modal/FormLogout.vue'));
const ClinicBranchSwitcher = defineAsyncComponent(() => import('~/components/Clinic/BranchSwitcher.vue'));

const props = defineProps<{
  isPersonal?: boolean
}>();

const authStore = useAuthStore();
const {isAuth, userRole} = storeToRefs(authStore);
const router = useRouter();
const {apiQuery} = useApi();
const { activeBranch, hasMultipleBranches, isSwitching, fetchBranches } = useClinicBranches()

if (import.meta.client) {
  authStore.restoreSession()
}

function readPersistedAuthSession(): { role: TUserRole } | null {
  if (!import.meta.client) {
    return null
  }

  try {
    const raw = localStorage.getItem('authSession')
    if (!raw) {
      return null
    }

    const { exp, role } = JSON.parse(raw) as { exp?: number; role?: TUserRole }
    if (typeof exp !== 'number' || exp * 1000 <= Date.now()) {
      return null
    }

    return { role: role ?? null }
  } catch {
    return null
  }
}

const showAuthUi = computed(() =>
  isAuth.value || readPersistedAuthSession() !== null,
)

const effectiveUserRole = computed(() =>
  userRole.value ?? readPersistedAuthSession()?.role ?? null,
)

const showBranchSwitcherInHeader = computed(() =>
  props.isPersonal
  && effectiveUserRole.value === 'clinic'
  && (hasMultipleBranches.value || isSwitching.value),
)

const branchSwitcherTitle = computed(() =>
  activeBranch.value?.name?.trim() || 'Клиника',
)

onMounted(() => {
  if (userRole.value === 'clinic' && props.isPersonal) {
    fetchBranches()
  }
})

const profileLink = computed(() => {
  if(userRole.value === 'doctor') return '/profile-doctor/';
  if(userRole.value === 'clinic') return '/profile-clinic/';
  return '/profile/';
})

const {open: showVetAuth, close: closeVetAuth} = useModal({
  component: FormAuthPhone,
  attrs: {
    isDoctor: true,
    onClose(success?: boolean) {
      closeVetAuth()
      if(success) {
        router.push({path: '/profile-doctor/'})
      }
    },
    onSwitchRole(role: 'doctor' | 'clinic' | 'client') {
      closeVetAuth();
      if (role === 'client') {
        setTimeout(() => showModal(), 100);
      }
    },
  }
});

const {open: showClinicAuth, close: closeClinicAuth} = useModal({
  component: FormAuthClinic,
  attrs: {
    onClose(success?: boolean) {
      closeClinicAuth()
      if(success) {
        router.push({path: '/profile-clinic/'})
      }
    },
  }
});

const {open: showModal, close} = useModal({
  component: FormAuthPhone,
  attrs: {
    async onClose(success?: boolean) {
      close()
      if(success) {
        await getUserProfile();
        router.push({path: '/profile/'})
      }
    },
    onSwitchRole(role: 'doctor' | 'clinic') {
      close();
      // vue-final-modal не поднимает новый инстанс в том же тике, где закрывается
      // предыдущий.
      setTimeout(() => (role === 'doctor' ? showVetAuth() : showClinicAuth()), 100);
    },
  }
});

const showAuthForm = () => {
  GAevent({
    category: 'Header',
    action: 'click',
    label: 'Authorization',
  });
  showModal();
}

const getUserProfile = async() => {
  await apiQuery<{data: TUserProfile}>('/client/profile/', 'GET', {})
  .then((data) => {
      authStore.setUserProfileData(data.data);
  })
}

const {open: showLogoutModal, close: closeLogoutModal} = useModal({
  component: FormLogout,
  attrs: {
    onClose() {
      closeLogoutModal();
    },
  }
});
</script>

<style lang="scss">
.header-profile {
  width: fit-content;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 24px;

  @include media(md) {
    gap: 12px;
  }

  &__icon {
    appearance: none;
    background: none;
    border: none;
    padding: 0;
    font-size: 24px;
    color: $color-accent-text;
    line-height: 0;
    cursor: pointer;

    svg {
      display: block;
      margin-bottom: 0;
      width: 24px;
      height: 24px;
      flex-shrink: 0;
    }

    @include media(header-nav, min-width) {
      display: none;
    }
  }

  &__button {
    text-wrap: nowrap;
    @include media(header-nav) {
      display: none;
    }
  }

  &__branch-switcher {
    @include media(header-nav) {
      display: none;
    }
  }

  @include media(header-nav) {
    gap: 12px;
    width: auto;

    &__logout {
      display: none;
    }
  }

  &--fallback {
    pointer-events: none;

    .header-profile__button,
    .header-profile__icon {
      cursor: default;
    }
  }

  &__fallback-account {
    display: none;
  }

  &__icon &__fallback-account {
    display: none;
  }
}

html[data-auth-session='true'] {
  .header-profile__fallback-login {
    display: none;
  }

  .header-profile__fallback-account {
    display: initial;
  }
}
</style>
